// Engine entry points: initState + the pure monthly reducer.
// System order is FIXED (determinism): commands → financing → ipo → people →
// nodes → tools → projects → rivals → markets → opex → close (street → board →
// stakeholders → talent → ma → history inside).
// See docs/ARCHITECTURE_M0.md §5, ARCHITECTURE_M2.md §2, ARCHITECTURE_M3.md §2.

import type { Command, ContentPack, Discipline, GameState, KpiSample, StepResult, TickReport } from "./types.js";
import { DISCIPLINES } from "./types.js";
import { seedRng } from "./prng.js";
import { makeCtx } from "./ctx.js";
import { applyCommands } from "./commands.js";
import { processFinancing, processOpex, closeTick } from "./systems/finance.js";
import { processIpo } from "./systems/ipo.js";
import { processPeople, agePeople, totalHeadcount } from "./systems/people.js";
import { processNodes } from "./systems/nodeAccess.js";
import { processTools } from "./systems/tools.js";
import { processTree } from "./systems/tree.js";
import { processDevContract } from "./systems/devContract.js";
import { closeGarageTick, processGarage } from "./systems/garage.js";
import { processProjects } from "./systems/projects.js";
import { processRivals, initRivals } from "./systems/rivals.js";
import { processMarkets } from "./systems/market.js";
import { validateContentPack } from "./content.js";

export function initState(content: ContentPack, seed: number): GameState {
  validateContentPack(content);
  const sc = content.scenario;
  const nodes: GameState["nodes"] = {};
  for (const n of content.nodes) {
    nodes[n.id] = {
      status: sc.preQualifiedNodes.includes(n.id) ? "qualified" : "locked",
      qualEmRemaining: 0,
      qualMonthsRemaining: 0,
      researchEm: 0,
      researchStaff: 0,
      reliabilityEm: 0,
      reliabilityStaff: 0,
    };
  }
  const markets: GameState["markets"] = {};
  for (const m of content.markets) markets[m.id] = { totalShare: 0, channelMonths: 0 };
  // garage scenarios (P0): no company yet — founders join their own company on
  // incorporation day; until then the pools are empty and cash is zero
  const g = sc.garage;
  const pools: GameState["people"]["pools"] = {
    design: { productive: g ? 0 : sc.founders.design, ramping: [] },
    verification: { productive: g ? 0 : sc.founders.verification, ramping: [] },
    implementation: { productive: g ? 0 : sc.founders.implementation, ramping: [] },
  };
  return {
    contentVersion: content.version,
    seed,
    tick: 0,
    monthIndex: sc.startMonthIndex,
    rng: seedRng(seed),
    cash: g ? 0 : sc.startingCash,
    dead: false,
    deathMonth: null,
    deathReason: null,
    path: null,
    stage: g ? "garage" : "angel",
    people: { pools, pendingHires: [] },
    nreEngineers: 0,
    nodes,
    projects: [],
    products: [],
    markets,
    finance: {
      founderShares: sc.founderShares,
      investorShares: 0,
      poolShares: 0,
      publicShares: 0,
      totalRaised: 0,
      founderShare: 1,
      roundsCompleted: 0,
      rounds: [],
      activeRaise: null,
      openTermSheet: null,
      lastPostMoney: 0,
      raiseCooldownMonths: 0,
      secondaryCooldownMonths: 0,
      everWorkingSilicon: false,
      ttmRevenue: [],
      burnHistory: [],
      exitFounderTake: null,
    },
    board: {
      confidence: content.economy.board.start,
      investorSeats: 0,
      hiringFreeze: false,
      lastMeetingMonth: null,
    },
    ipo: { process: null, pulls: 0, cooldownMonths: 0, lockupMonth: null, ipoMonth: null, ipoPrice: null },
    street: null,
    rivals: initRivals(content.rivals.roster),
    talent: {
      morale: content.economy.talent.moraleStart,
      compPolicy: 0,
      stars: [],
      integrationMonthsLeft: 0,
    },
    stakeholders: {
      foundryRel: content.economy.stakeholders.foundryStart,
      customers: Object.fromEntries(content.markets.map((m) => [m.id, content.economy.stakeholders.customerStart])),
      waferDiscountPct: 0,
      waferDiscountMonthsLeft: 0,
      volumeCommit: null,
      shareBoosts: [],
      supportPrograms: [],
      lastForecastBustMonth: null,
    },
    asks: [],
    askLog: {},
    tools: {
      edaTier: "standard",
      inhouse: {
        design: { em: 0, staff: 0, announced: false },
        verification: { em: 0, staff: 0, announced: false },
        implementation: { em: 0, staff: 0, announced: false },
      },
    },
    ma: { approachCooldownMonths: 0 },
    tree: { researched: {}, inProgress: {}, credits: {}, staffing: {} },
    garage: g
      ? {
          founders: g.founders.map((f) => ({ name: f.name, archetype: f.archetype, discipline: f.discipline })),
          savings: Math.round(g.potStart + g.founders.reduce((a, f) => a + f.savingsMonths * g.personalBurnMonthly, 0)),
          demoEm: 0,
          demoStage: 0,
          discoveryEm: 0,
          discovered: [],
          networkingEm: 0,
          warmIntro: 0,
          introSpent: 0,
          recruitEm: 0,
          committedHires: { design: 0, verification: 0, implementation: 0 },
          researchNodeId: null,
          alloc: {},
          lastPitchMonth: null,
          pitchesFailed: 0,
        }
      : null,
    garageLegacy: null,
    devContract: null,
    counters: { nextProject: 1, shippedProjects: 0, nextAsk: 1, nextRival: 1, nextStar: 1 },
    kpiHistory: [],
  };
}

export function step(state: GameState, commands: Command[], content: ContentPack): StepResult {
  if (state.contentVersion !== content.version) {
    throw new Error(`content version mismatch: state=${state.contentVersion} pack=${content.version}`);
  }
  if (state.dead) {
    const emptyAlloc = { budget: 0, nre: 0, sustaining: 0, qual: 0, research: 0, reliability: 0, tools: 0, tree: 0, byProject: {}, idle: 0 };
    const report: TickReport = {
      month: state.monthIndex,
      ledger: [],
      events: [],
      rejectedCommands: commands.map((command) => ({ command, reason: "company is dead" })),
      allocation: { design: { ...emptyAlloc }, verification: { ...emptyAlloc }, implementation: { ...emptyAlloc } },
      productSales: [],
      kpi: state.kpiHistory[state.kpiHistory.length - 1] ?? emptyKpi(state),
    };
    return { state, report };
  }

  const s = structuredClone(state);
  const ctx = makeCtx(s, content);

  applyCommands(ctx, commands);
  // an accepted acquisition ends the campaign mid-tick: the world stops here.
  // P0: garage months short-circuit the company pipeline — there IS no company
  // yet (accepting the seed sheet mid-tick incorporates and runs the full
  // pipeline the same month, which is exactly incorporation day should feel).
  if (!s.dead && s.stage === "garage") {
    processGarage(ctx);
    closeGarageTick(ctx);
  } else {
    if (!s.dead) {
      processFinancing(ctx);
      processIpo(ctx);
      processPeople(ctx);
      processNodes(ctx);
      processTools(ctx);
      processTree(ctx);
      processDevContract(ctx);
      processProjects(ctx);
      processRivals(ctx);
      processMarkets(ctx);
      processOpex(ctx);
      agePeople(ctx);
    }
    closeTick(ctx);
  }

  // exact conservation check (also asserted by property tests)
  const ledgerSum = ctx.ledger.reduce((a, e) => a + e.amount, 0);
  if (s.cash !== ctx.cashAtTickStart + ledgerSum) {
    throw new Error(`ledger conservation violated: cash=${s.cash} expected=${ctx.cashAtTickStart + ledgerSum}`);
  }

  for (const d of DISCIPLINES) {
    ctx.allocation[d].idle = Math.max(0, ctx.emBudget[d]); // whatever no system consumed
  }

  const report: TickReport = {
    month: ctx.ledger.length > 0 ? ctx.ledger[0]!.month : s.monthIndex - ctx.monthsPerTick,
    ledger: ctx.ledger,
    events: ctx.events,
    rejectedCommands: ctx.rejected,
    allocation: ctx.allocation,
    productSales: ctx.productSales,
    kpi: s.kpiHistory[s.kpiHistory.length - 1]!,
  };
  return { state: s, report };
}

function emptyKpi(state: GameState): KpiSample {
  const headByDiscipline = (d: Discipline) => state.people.pools[d].productive + state.people.pools[d].ramping.reduce((x, r) => x + r.count, 0);
  return {
    month: state.monthIndex,
    cash: state.cash,
    revenue: 0,
    nreRevenue: 0,
    cogs: 0,
    opex: 0,
    headcount: DISCIPLINES.reduce((a, d) => a + headByDiscipline(d), 0),
    share: 0,
    runwayMonths: 0,
    activeProjects: 0,
    shippedProducts: state.products.length,
    shareByMarket: {},
    headcountByDiscipline: Object.fromEntries(DISCIPLINES.map((d) => [d, headByDiscipline(d)])) as Record<Discipline, number>,
  };
}

// re-export for convenience in harness/tests
export { totalHeadcount };
